import moment from 'moment';
import { defaultStaticRanges } from 'react-date-range';
import {
  DATE_QUERY_DAYS_KEY,
  DATE_QUERY_FROM_KEY,
  applyDateFiltersToSearchParams,
  parseDateFiltersFromSearchParams,
} from '../hooks/useDateFilterUrlParams';

// Define StaticRange type locally since it might not be exported
interface StaticRange {
  label: string;
  range: () => { startDate: Date; endDate: Date };
  hasCustomRendering?: boolean;
  isSelected?: (range: { startDate?: Date; endDate?: Date }) => boolean;
}

const attachSelectedBehavior = (ranges: StaticRange[]): StaticRange[] => {
  return ranges.map((range) => {
    const staticRange = {
      ...range,
      isSelected(selectedRange: { startDate?: Date; endDate?: Date }) {
        try {
          const definedRange = range.range();
          return !!(
            selectedRange?.startDate &&
            selectedRange?.endDate &&
            moment(selectedRange.startDate).isSame(moment(definedRange.startDate), "day") &&
            moment(selectedRange.endDate).isSame(moment(definedRange.endDate), "day")
          );
        } catch (error) {
          console.error(`Error checking selected state for ${range.label}:`, error);
          return false;
        }
      },
    } as StaticRange;

    return staticRange;
  });
};

export const buildCombinedStaticRanges = (customStaticRanges: StaticRange[] = []): StaticRange[] => {
  try {
    const baseStaticRanges = Array.isArray(defaultStaticRanges) ? defaultStaticRanges : [];
    return [
      ...baseStaticRanges.filter(
        (defRange) => !["Last Week", "This Week", "This Month"].includes(defRange.label)
      ),
      ...customStaticRanges,
    ];
  } catch (error) {
    console.error('Error creating combined static ranges:', error);
    return [...customStaticRanges];
  }
};

// Types
export interface DateRange {
  startDate: Date;
  endDate: Date;
}

export interface DateRangeObject {
  startDate: Date;
  endDate: Date;
  key: string;
}

export interface APIDateFormat {
  start_date: string;
  end_date: string;
  count: number;
}

export interface FilterState {
  startDate: string;
  endDate: string;
  count: string;
}

// Helper function to format date for display
export const formatDateForDisplay = (date: Date | string | moment.Moment | null | undefined): string => {
  if (!date) return '';
  // Ensure we're working with a proper date object and format consistently
  // Use local timezone to prevent date shifting
  const dateObj = moment.isMoment(date) ? date : moment(date);
  return dateObj.format('M/D/YY');
};

// Helper function to format date range for display
export const formatDateRangeForDisplay = (startDate: Date | string | moment.Moment | null | undefined, endDate: Date | string | moment.Moment | null | undefined): string => {
  if (!startDate || !endDate) return '';
  return `${formatDateForDisplay(startDate)} - ${formatDateForDisplay(endDate)}`;
};

// Helper function to check if date range is valid
export const isValidDateRange = (startDate: Date | string | moment.Moment | null | undefined, endDate: Date | string | moment.Moment | null | undefined): boolean => {
  if (!startDate || !endDate) return false;
  return moment(startDate).isSameOrBefore(moment(endDate));
};

// Helper function to get default date range (7 days before today)
export const getDefaultDateRange = (): DateRange => {
  const today = new Date();
  const startDate = new Date(today);
  startDate.setDate(today.getDate() - 6);
  return { startDate, endDate: today };
};

// Helper function to create custom static ranges
export const createCustomStaticRanges = (
  currentStartDate?: Date | string | moment.Moment | null | undefined,
  currentEndDate?: Date | string | moment.Moment | null | undefined
): StaticRange[] => {
  const getCurrentRange = () => {
    const startDate = currentStartDate ? moment(currentStartDate).toDate() : new Date();
    const endDate = currentEndDate ? moment(currentEndDate).toDate() : startDate;
    return { startDate, endDate };
  };

  const presetRanges = attachSelectedBehavior([
    {
      label: "Last 7 days",
      range: () => {
        try {
          const endDate = moment().toDate();
          const startDate = moment().subtract(6, "days").toDate();
          return { startDate, endDate };
        } catch (error) {
          console.error('Error creating Last 7 days range:', error);
          const today = new Date();
          return { startDate: today, endDate: today };
        }
      },
    },
    {
      label: "Last 30 days",
      range: () => {
        try {
          const endDate = moment().toDate();
          const startDate = moment().subtract(29, "days").toDate();
          return { startDate, endDate };
        } catch (error) {
          console.error('Error creating Last 30 days range:', error);
          const today = new Date();
          return { startDate: today, endDate: today };
        }
      },
    },
    {
      label: "Last 60 days",
      range: () => {
        try {
          const endDate = moment().toDate();
          const startDate = moment().subtract(59, "days").toDate();
          return { startDate, endDate };
        } catch (error) {
          console.error('Error creating Last 60 days range:', error);
          const today = new Date();
          return { startDate: today, endDate: today };
        }
      },
    },
    {
      label: "Last 90 days",
      range: () => {
        try {
          const endDate = moment().toDate();
          const startDate = moment().subtract(89, "days").toDate();
          return { startDate, endDate };
        } catch (error) {
          console.error('Error creating Last 90 days range:', error);
          const today = new Date();
          return { startDate: today, endDate: today };
        }
      },
    },
  ]);

  return [
    ...presetRanges,
    {
      label: "Custom",
      range: () => {
        try {
          return getCurrentRange();
        } catch (error) {
          console.error('Error creating Custom range:', error);
          const today = new Date();
          return { startDate: today, endDate: today };
        }
      },
      hasCustomRendering: true,
      isSelected: () => false,
    },
  ];
};

// Helper function to find matching static range
export const findMatchingStaticRange = (
  startDate: Date | string | moment.Moment | null | undefined, 
  endDate: Date | string | moment.Moment | null | undefined, 
  staticRanges: StaticRange[]
): StaticRange | null => {
  try {
    if (!startDate || !endDate) return null;
    
    if (!Array.isArray(staticRanges)) {
      console.error('staticRanges is not an array:', staticRanges);
      return null;
    }
    
    return staticRanges.find(
      (range) => {
        try {
          if (!range || typeof range.range !== 'function') {
            console.error('Invalid range object:', range);
            return false;
          }
          
          const rangeResult = range.range();
          if (!rangeResult || !rangeResult.startDate || !rangeResult.endDate) {
            console.error('Invalid range result:', rangeResult);
            return false;
          }
          
          return moment(rangeResult.startDate).isSame(moment(startDate), "day") &&
                 moment(rangeResult.endDate).isSame(moment(endDate), "day");
        } catch (error) {
          console.error('Error in range comparison:', error);
          return false;
        }
      }
    ) || null;
  } catch (error) {
    console.error('Error in findMatchingStaticRange:', error);
    return null;
  }
};

// Helper function to convert date range to API format
export const convertToAPIFormat = (
  startDate: Date | string | moment.Moment | null | undefined, 
  endDate: Date | string | moment.Moment | null | undefined
): APIDateFormat => {
  if (!startDate || !endDate) return { start_date: '', end_date: '', count: 0 };
  
  return {
    start_date: moment(startDate).format('YYYY-MM-DD'),
    end_date: moment(endDate).format('YYYY-MM-DD'),
    count: moment(endDate).diff(moment(startDate), 'days') + 1,
  };
};

// Helper function to validate and parse date from string
export const parseDateSafely = (dateString: string | null | undefined): Date | null => {
  if (!dateString) return null;
  
  const parsed = moment(dateString);
  return parsed.isValid() ? parsed.toDate() : null;
};

// Helper function to create date range object for react-date-range
export const createDateRangeObject = (
  startDate: Date | string | moment.Moment | null | undefined, 
  endDate: Date | string | moment.Moment | null | undefined
): DateRangeObject[] => {
  // Ensure dates are properly handled without timezone issues
  const createDate = (dateInput: Date | string | moment.Moment | null | undefined): Date => {
    if (!dateInput) return new Date();
    
    // If it's already a Date object, return it
    if (dateInput instanceof Date) return dateInput;
    
    // If it's a string, parse it properly without timezone conversion
    if (typeof dateInput === 'string') {
      // Remove timezone suffix if present to prevent shifting
      const cleanDateString = dateInput.replace(/T\d{2}:\d{2}:\d{2}.\d{3}Z?$/, '');
      const parsed = moment(cleanDateString);
      return parsed.isValid() ? parsed.toDate() : new Date();
    }
    
    // For moment objects, convert to Date
    if (moment.isMoment(dateInput)) {
      return dateInput.isValid() ? dateInput.toDate() : new Date();
    }
    
    // For any other type, return current date
    return new Date();
  };

  return [
    {
      startDate: createDate(startDate),
      endDate: createDate(endDate),
      key: "selection",
    },
  ];
};

// Migration utility: Convert old Redux state to new format
export const migrateFromReduxState = (reduxState: { startDate?: string; endDate?: string } | null | undefined): FilterState | null => {
  if (!reduxState) return null;
  
  const { startDate, endDate } = reduxState;
  if (!startDate || !endDate) return null;
  
  return {
    startDate: moment(startDate).format('YYYY-MM-DD') + 'T00:00:00.000Z',
    endDate: moment(endDate).format('YYYY-MM-DD') + 'T23:59:59.000Z',
    count: (moment(endDate).diff(moment(startDate), 'days') + 1).toString(),
  };
};

// Migration utility: Convert old URL parameters to new format
export const migrateFromURLParams = (urlParams: { startDate?: string; endDate?: string; count?: string } | null | undefined): FilterState | null => {
  if (!urlParams) return null;
  
  const { startDate, endDate, count } = urlParams;
  if (!startDate || !endDate) return null;
  
  return {
    startDate: moment(startDate).format('YYYY-MM-DD') + 'T00:00:00.000Z',
    endDate: moment(endDate).format('YYYY-MM-DD') + 'T23:59:59.000Z',
    count: count || (moment(endDate).diff(moment(startDate), 'days') + 1).toString(),
  };
};

// Utility to check if filters are active (non-default)
export const areFiltersActive = (filters: FilterState | null | undefined): boolean => {
  if (!filters) return false;
  
  const defaultRange = getDefaultDateRange();
  const defaultStartDate = moment(defaultRange.startDate).format('YYYY-MM-DD') + 'T00:00:00.000Z';
  const defaultEndDate = moment(defaultRange.endDate).format('YYYY-MM-DD') + 'T23:59:59.000Z';
  const defaultCount = (moment(defaultRange.endDate).diff(moment(defaultRange.startDate), 'days') + 1).toString();
  
  return !!(
    filters.startDate !== defaultStartDate ||
    filters.endDate !== defaultEndDate ||
    filters.count !== defaultCount
  );
};

// Utility to get filter summary for display
export const getFilterSummary = (filters: FilterState | null | undefined): string => {
  if (!filters || !filters.startDate || !filters.endDate) {
    return 'No date range selected';
  }
  
  const startDate = formatDateForDisplay(filters.startDate);
  const endDate = formatDateForDisplay(filters.endDate);
  const count = filters.count || (moment(filters.endDate).diff(moment(filters.startDate), 'days') + 1).toString();
  
  return `${startDate} - ${endDate} (${count} days)`;
};

// Utility to check if URL has date parameters
export const hasDateParamsInURL = (): boolean => {
  const urlParams = new URLSearchParams(window.location.search);
  return !!parseDateFiltersFromSearchParams(urlParams);
};

// Utility to get date parameters from URL
export const getDateParamsFromURL = (): { startDate: string | null; endDate: string | null; count: string | null } => {
  const urlParams = new URLSearchParams(window.location.search);
  const parsed = parseDateFiltersFromSearchParams(urlParams);
  return {
    startDate: parsed?.startDate ?? null,
    endDate: parsed?.endDate ?? null,
    count: parsed?.count ?? null,
  };
};

// Utility to set date parameters in URL
export const setDateParamsInURL = (startDate: string, endDate: string, count?: string): void => {
  const urlParams = new URLSearchParams(window.location.search);
  const nextParams = applyDateFiltersToSearchParams(urlParams, { startDate, endDate, count });
  
  const newURL = `${window.location.pathname}?${nextParams.toString()}`;
  window.history.replaceState({}, '', newURL);
};

// Utility to clear date parameters from URL
export const clearDateParamsFromURL = (): void => {
  const urlParams = new URLSearchParams(window.location.search);
  urlParams.delete(DATE_QUERY_FROM_KEY);
  urlParams.delete(DATE_QUERY_DAYS_KEY);
  // Remove legacy keys if they exist in old URLs
  urlParams.delete('startDate');
  urlParams.delete('endDate');
  urlParams.delete('count');
  
  const newURL = urlParams.toString() ? `${window.location.pathname}?${urlParams.toString()}` : window.location.pathname;
  window.history.replaceState({}, '', newURL);
};

// Utility to ensure date parameters are always in URL (for route changes)
export const ensureDateParamsInURL = (startDate: string, endDate: string, count?: string): void => {
  const urlParams = new URLSearchParams(window.location.search);
  const nextParams = startDate && endDate
    ? applyDateFiltersToSearchParams(urlParams, { startDate, endDate, count })
    : urlParams;
  const urlChanged = nextParams.toString() !== urlParams.toString();

  if (urlChanged) {
    const newURL = `${window.location.pathname}?${nextParams.toString()}`;
    window.history.replaceState({}, '', newURL);
  }
};

// Utility to sync date parameters to URL without triggering navigation
export const syncDateParamsToURL = (startDate: string, endDate: string, count?: string): void => {
  const urlParams = new URLSearchParams(window.location.search);
  
  // Always set the date parameters
  const nextParams = applyDateFiltersToSearchParams(urlParams, { startDate, endDate, count });
  
  const newURL = `${window.location.pathname}?${nextParams.toString()}`;
  window.history.replaceState({}, '', newURL);
};

// Utility to check if current URL has the same date parameters
export const hasSameDateParamsInURL = (startDate: string, endDate: string, count?: string): boolean => {
  const urlParams = new URLSearchParams(window.location.search);
  const parsed = parseDateFiltersFromSearchParams(urlParams);
  const urlStartDate = parsed?.startDate ?? null;
  const urlEndDate = parsed?.endDate ?? null;
  const urlCount = parsed?.count ?? null;
  
  return urlStartDate === startDate && 
         urlEndDate === endDate && 
         urlCount === (count || null);
};

// Utility to validate date parameters
export const validateDateParams = (startDate: string, endDate: string): boolean => {
  try {
    const start = moment(startDate);
    const end = moment(endDate);
    return start.isValid() && end.isValid() && start.isSameOrBefore(end);
  } catch (error) {
    console.error('Error validating date params:', error);
    return false;
  }
};

// Utility to get date range from static range selection
export const getDateRangeFromStaticRange = (staticRange: any): { startDate: Date; endDate: Date } | null => {
  try {
    if (staticRange && typeof staticRange.range === 'function') {
      const range = staticRange.range();
      if (range && range.startDate && range.endDate) {
        return {
          startDate: range.startDate instanceof Date ? range.startDate : new Date(range.startDate),
          endDate: range.endDate instanceof Date ? range.endDate : new Date(range.endDate),
        };
      }
    }
    return null;
  } catch (error) {
    console.error('Error getting date range from static range:', error);
    return null;
  }
}; 
